<template>
  <div class="py-1 border-b border-slate-400 flex">
    <div
      v-if="!isEdit"
      class="relative h-10 px-4 flex items-center justify-between flex-grow gap-6 rounded hover:bg-slate-200 overflow-hidden"
      :class="{ 'pointer-events-none': !currentUser.role.includes('admin') }"
      @mouseenter.prevent="isHover = true"
      @mouseleave.prevent="isHover = false"
    >
      <div class="flex max-w-152 truncate">
        <div
          class="py-1 px-2 flex items-center rounded truncate"
          :style="{ backgroundColor: branch.background_color }"
        >
          <BlTextBody2
            class="truncate"
            :title="branch.name"
            :text="branch.name"
            :style="{ color: branch.color }"
          />
        </div>
      </div>
      <div
        class="absolute px-2 flex items-center gap-2 transition-all duration-300 bg-slate-200"
        :class="{
          'right-2 opacity-100': isHover,
          '-right-200 opacity-0': !isHover
        }"
      >
        <BlInputButton
          label="Delete"
          type="outlined-error"
          dense
          @clicked="deleteOpened = true"
        />
        <BlInputButton label="Edit" type="outlined" dense @clicked="openEdit" />
      </div>
    </div>
    <div
      v-else
      id="edit-state"
      class="h-10 px-4 flex items-center justify-between flex-grow gap-6 rounded bg-slate-200"
    >
      <div class="flex items-center flex-grow gap-4">
        <BlInputText :input="nameInput" @changed="inputHandler" />
        <div class="relative">
          <div
            :data-id="`color-branch-tag`"
            :class="[colorOpened ? 'border-light-purple' : 'border-slate-500']"
            class="relative h-10 w-24 px-4 flex items-center justify-between bg-pure-white border rounded cursor-pointer"
            @click.prevent="colorOpened = !colorOpened"
          >
            <div
              class="w-6 h-6 flex-shrink-0 rounded-full border border-slate-300"
              :style="{ backgroundColor: colorInput.backgroundColor }"
            />
            <BlIcon
              :data-id="`color-branch-tag`"
              class="absolute right-4 w-5 h-5 transition-all duration-300"
              icon="chevron-down"
              :style="{ rotate: colorOpened ? '180deg' : '0deg' }"
            />
          </div>
          <div
            :data-id="`color-branch-tag`"
            v-if="colorOpened"
            class="absolute right-0 w-54 mt-2 z-10 p-4 bg-pure-white rounded border border-grey-200 shadow-md"
          >
            <div
              :data-id="`color-branch-tag`"
              class="grid grid-cols-6 flex-wrap gap-2"
            >
              <button
                :data-id="`color-branch-tag`"
                v-for="item in colorOptions"
                :key="item.color"
                class="h-6 w-6 flex-shrink-0 rounded border outline-none"
                :class="[
                  colorInput.color === item.color
                    ? 'border-light-purple'
                    : 'border-transparent'
                ]"
                :style="{ backgroundColor: item.backgroundColor }"
                @click.prevent="colorInput = item"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="flex items-center flex-shrink-0 gap-2">
        <BlInputButton label="Cancel" type="plain" @clicked="isEdit = false" />
        <BlInputButton
          label="Update"
          type="outlined"
          dense
          :loading="submitLoading"
          :disabled="
            !nameInput.model ||
              (nameInput.model === branch.name &&
                colorInput.color === branch.color)
          "
          @clicked="onUpdate"
        />
      </div>
    </div>
    <BlBackdrop
      v-if="deleteOpened"
      title="Delete tag"
      label="Delete the tag"
      type="error"
      :disabled="submitLoading"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onRemove"
    >
      <div class="flex flex-wrap gap-1 px-8">
        <BlTextBody2 text="You are about to delete" />
        <BlTextBody2 class="font-bold" :text="branch.name" />
        <BlTextBody2 text="from the repository." />
        <BlTextBody2 text="Are you sure you want to proceed?" />
        <BlTextBody2 text="This action is irreversible." />
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import BlBackdrop from "../../BlBackdrop.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlIcon from "../../BlIcon.vue";

export default {
  name: "BranchTagItem",
  props: ["branch"],
  components: {
    BlBackdrop,
    BlTextBody2,
    BlInputText,
    BlInputButton,
    BlIcon
  },
  data() {
    return {
      submitLoading: false,
      isHover: false,
      isEdit: false,
      deleteOpened: false,
      nameInput: {
        placeholder: "Type a name",
        model: ""
      },
      colorOpened: false,
      colorInput: { color: "#C73D3D", backgroundColor: "#FBE5E5" },
      colorOptions: [
        { color: "#327D0E", backgroundColor: "#E0FCCB" },
        { color: "#059669", backgroundColor: "#D1FAE5" },
        { color: "#708308", backgroundColor: "#FCFFC8" },
        { color: "#EC4A0A", backgroundColor: "#FFEAD5" },
        { color: "#C73D3D", backgroundColor: "#FBE5E5" },
        { color: "#AF5D30", backgroundColor: "#F3E6D2" }
      ]
    };
  },
  computed: {
    ...mapGetters(["currentUser"])
  },
  watch: {
    colorOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    }
  },
  methods: {
    ...mapActions(["updateBranch", "deleteBranch"]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    closeHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (`color-branch-tag` === id) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closeHandler);
        this.colorOpened = false;
      }
    },
    //
    openEdit() {
      this.isEdit = true;
      this.nameInput.model = this.branch.name;
      this.colorInput.color = this.branch.color;
      this.colorInput.backgroundColor = this.branch.background_color;
    },
    onUpdate() {
      this.submitLoading = true;
      const data = {
        id: this.branch.id,
        branch: {
          name: this.nameInput.model,
          color: this.colorInput.color,
          background_color: this.colorInput.backgroundColor
        }
      };
      this.updateBranch(data).then(({ success }) => {
        this.submitLoading = false;
        if (!success) return;
        this.isEdit = false;
      });
    },
    onRemove() {
      this.submitLoading = true;
      const data = { id: this.branch.id };
      this.deleteBranch(data).then(({ success }) => {
        this.submitLoading = false;
        if (!success) return;
        this.deleteOpened = false;
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/repository/tags/BranchTag.vue